import "./CameraScanner.scss";

import { BrowserMultiFormatReader } from "@zxing/library";
import { FC, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Close } from "@/assets/icons/icons";
import { Modal } from "@/components/Interface/Modal/Modal";

interface CameraScannerProps {
  onScan: (value: string) => void;
}

export const CameraScanner: FC<CameraScannerProps> = ({ onScan }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch {
      setError(t("Onboarding.sections.sim-verification.scanner.cameraError"));
    }
  }, [t]);

  const handleOpen = () => {
    setIsOpen(true);
    setError(null);
    setTimeout(() => startCamera(), 100);
  };

  const handleClose = () => {
    stopCamera();
    setIsOpen(false);
    setIsScanning(false);
    setError(null);
  };

  const performScan = async () => {
    if (!videoRef.current || isScanning) return;

    setIsScanning(true);
    setError(null);

    try {
      const codeReader = new BrowserMultiFormatReader();
      const canvas = document.createElement("canvas");
      const video = videoRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");

      if (!ctx) return;

      ctx.drawImage(video, 0, 0);
      const imageDataUrl = canvas.toDataURL("image/png");
      const result = await codeReader.decodeFromImageUrl(imageDataUrl);

      if (result?.getText()) {
        const barcodeText = result.getText();
        const numbers = barcodeText.replace(/\D/g, "");

        if (numbers.length >= 19) {
          const iccid = numbers.substring(0, 20);
          onScan(iccid);
          handleClose();

          return;
        }
      }

      setError(t("Onboarding.sections.sim-verification.scanner.noBarcode"));
    } catch {
      setError(t("Onboarding.sections.sim-verification.scanner.noBarcode"));
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <>
      <button
        className="camera-scanner-trigger"
        type="button"
        onClick={handleOpen}
      >
        <ScanIcon />
        {t("Onboarding.sections.sim-verification.scanner.button")}
      </button>

      {isOpen && (
        <Modal
          title={t("Onboarding.sections.sim-verification.scanner.title")}
          onClose={handleClose}
          hasScroll={false}
        >
          <div className="camera-scanner-content">
            <div className="camera-scanner-video-container">
              <video
                className="camera-scanner-video"
                ref={videoRef}
                autoPlay
                playsInline
                muted
              />
              <div
                className={`camera-scanner-overlay ${isScanning ? "scanning" : ""}`}
              />
            </div>

            <p className="camera-scanner-hint">
              {t("Onboarding.sections.sim-verification.scanner.hint")}
            </p>

            {error && (
              <div className="camera-scanner-error">
                <span>{error}</span>
                <button
                  className="camera-scanner-error-close"
                  type="button"
                  aria-label={t("common.buttons.abort")}
                  onClick={() => setError(null)}
                >
                  <Close className="h-3 w-3" />
                </button>
              </div>
            )}

            <div className="camera-scanner-actions">
              <button
                className="camera-scanner-scan-button"
                type="button"
                disabled={isScanning}
                onClick={performScan}
              >
                {isScanning
                  ? t("Onboarding.sections.sim-verification.scanner.scanning")
                  : t("Onboarding.sections.sim-verification.scanner.scan")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

const ScanIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7V5a2 2 0 0 1 2-2h2" />
    <path d="M17 3h2a2 2 0 0 1 2 2v2" />
    <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
    <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
    <line x1="4" y1="12" x2="20" y2="12" />
  </svg>
);
